/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import captureClient from '../api/captureClient';

/**
 * Parse user agent to extract OS, browser, and device type
 */
const parseUserAgent = (userAgent) => {
  const ua = userAgent || navigator.userAgent;

  // Detect OS
  let os = 'Unknown';
  if (ua.includes('Windows NT 10.0')) { os = 'Windows 10'; }
  else if (ua.includes('Windows NT 6.3')) { os = 'Windows 8.1'; }
  else if (ua.includes('Windows NT 6.2')) { os = 'Windows 8'; }
  else if (ua.includes('Windows NT 6.1')) { os = 'Windows 7'; }
  else if (ua.includes('Windows')) { os = 'Windows'; }
  else if (ua.includes('Mac OS X')) {
    const match = ua.match(/Mac OS X (\d+[._]\d+)/);
    os = match ? `macOS ${match[1].replace('_', '.')}` : 'macOS';
  } else if (ua.includes('Android')) {
    const match = ua.match(/Android (\d+(\.\d+)?)/);
    os = match ? `Android ${match[1]}` : 'Android';
  } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
    const match = ua.match(/OS (\d+_\d+)/);
    os = match ? `iOS ${match[1].replace('_', '.')}` : 'iOS';
  } else if (ua.includes('Linux')) {
     os = 'Linux'; 
  } else { os = 'Unknown'; }

  // Detect Browser
  let browser = 'Unknown';
  if (ua.includes('Edg/')) {
    const match = ua.match(/Edg\/(\d+)/);
    browser = match ? `Edge ${match[1]}` : 'Edge';
  } else if (ua.includes('Chrome/') && !ua.includes('Edg')) {
    const match = ua.match(/Chrome\/(\d+)/);
    browser = match ? `Chrome ${match[1]}` : 'Chrome';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    const match = ua.match(/Version\/(\d+)/);
    browser = match ? `Safari ${match[1]}` : 'Safari';
  } else if (ua.includes('Firefox/')) {
    const match = ua.match(/Firefox\/(\d+)/);
    browser = match ? `Firefox ${match[1]}` : 'Firefox';
  } else if (ua.includes('MSIE') || ua.includes('Trident/')) {
    browser = 'Internet Explorer';
  } else{
    browser = 'Unknown';
  }

  // Detect Device Type
  let deviceType = 'desktop';
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    deviceType = 'tablet';
  } else if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    deviceType = 'mobile';
  } else{
    deviceType = 'desktop';
  }

  return { os, browser, deviceType };
};

/**
 * Get connection type from Network Information API
 */
const getConnectionType = () => {
  if ('connection' in navigator) {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn && conn.effectiveType) {
      return conn.effectiveType; // '4g', '3g', '2g', 'slow-2g'
    }
  }
  return null;
};

/**
 * Collect all provenance metadata
 * @param {Object} location - Optional location object from Capture.jsx {latitude, longitude, accuracy}
 */
export const captureProvenanceMetadata = async (location = null) => {
  const userAgent = navigator.userAgent;
  const { os, browser, deviceType } = parseUserAgent(userAgent);
  const connectionType = getConnectionType();

  return {
    userAgent,
    os,
    browser,
    deviceType,
    connectionType,
    ...(location ? { ...location, locationSource: 'gps' } : {}),
  };
};

export const sendProvenanceEvent = async (assignmentToken, eventType, location = null, retries = 2) => {
  try {
    const metadata = await captureProvenanceMetadata(location);

    await captureClient.post(`/${assignmentToken}/provenance`, {
      eventType,
      metadata,
    });

    return { success: true };
  } catch (error) {
    console.error('Failed to send provenance event:', error);

    // Retry logic
    if (retries > 0) {
      console.log(`Retrying provenance event... (${retries} attempts left)`);
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait 1 second
      return sendProvenanceEvent(assignmentToken, eventType, location, retries - 1);
    }

    return { success: false, error };
  }
};

export default {
  captureProvenanceMetadata,
  sendProvenanceEvent,
};
